import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText, Upload, Download, Search, Folder,
  File, Calendar, Database, Shield,
  Plus, X, AlertCircle, Edit3, Sparkles,
  ThumbsUp, ThumbsDown
} from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import documentStorage, { extractTextFromPDF, isPdfDocument } from '../../services/documentStorage';
import { readDocumentWithAI } from '../../services/documentReader';
import { runIngestionPipeline } from '../../rag/ingestionPipeline';
import { documentIntelligence } from '../../rag/documentIntelligence';
import { ingestionValidator } from '../../rag/ingestionValidator';
import { documentsService } from '../../services/documentsService';
import { useAuth } from '../../contexts/AuthContext';
import { useHOA } from '../../contexts/HOAContext';
import ConfirmDialog from '../common/ConfirmDialog';
import { runRAGQuery } from '../../rag/ragEngine';
import { waitForIngestionReady, generateStressTestQuestions, runStressTest } from '../../rag/level4StressTest';
import { supabase } from '../../lib/supabase';
import { conversationManager } from '../../rag/conversationManager';
import PdfViewer from './PdfViewer';
import EmbeddingHealthTile from './EmbeddingHealthTile';
import { logEvent } from '../../lib/activityLogger';
import { rateAnswer } from '../../services/ragReviewService';
import { notificationService } from '../../services/notificationService';


// Maps UI folder display names/IDs to valid Supabase category enum values (single primary)
const FOLDER_TO_CATEGORY = {
  'governing-documents': 'ccr',
  'governing_documents': 'ccr',
  'Governing Documents': 'ccr',
  'Governing': 'ccr',
  'amendments': 'amendment',
  'Amendments': 'amendment',
  'bylaws': 'bylaw',
  'Bylaws': 'bylaw',
  'resolutions': 'resolution',
  'Resolutions': 'resolution',
  'minutes': 'minutes',
  'Meeting Minutes': 'minutes',
  'financial': 'financial',
  'Financial Reports': 'financial',
  'Financial': 'financial',
  'financial-reports': 'financial',
  'legal': 'legal',
  'Legal': 'legal',
  'Legal & Contracts': 'legal',
  'acc': 'acc',
  'ACC Documents': 'acc',
  'notices': 'notice',
  'Notices': 'notice',
  'contracts': 'contract',
  'Contracts': 'contract',
  'insurance': 'insurance',
  'Insurance': 'insurance',
  'other': 'other',
  'Other': 'other',
};

// Maps folder keys to ALL categories they contain (multi-category support)
const FOLDER_MULTI_CATEGORIES = {
  'all': [],
  'governing': ['ccr', 'amendment', 'bylaw', 'resolution'],
  'plat-maps-parcels': ['other'],
  'financial': ['financial'],
  'legal-contracts': ['legal', 'contract', 'insurance', 'notice'],
  'meeting-minutes': ['minutes'],
  'acc-documents': ['acc'],
  'Governing': ['ccr', 'amendment', 'bylaw'],
  'Legal & Contracts': ['legal', 'contract', 'insurance'],
  'Financial': ['financial', 'minutes'],
  'Meeting Minutes': ['minutes'],
  'ACC Documents': ['acc'],
  'Notices': ['notice', 'resolution'],
  'Other': ['other'],
};

const DEFAULT_FOLDERS = {
  'All': { name: 'All Documents', icon: FileText, color: 'blue', children: [], system_key: 'all', dbId: null },
  'Governing': { name: 'Governing Documents', icon: Shield, color: 'red', children: [], system_key: 'governing', dbId: null },
  'Financial': { name: 'Financial Reports', icon: Database, color: 'green', children: [], system_key: 'financial', dbId: null },
  'Legal & Contracts': { name: 'Legal & Contracts', icon: Folder, color: 'purple', children: [], system_key: 'legal-contracts', dbId: null },
  'Meeting Minutes': { name: 'Meeting Minutes', icon: Calendar, color: 'slate', children: [], system_key: 'meeting-minutes', dbId: null },
};

const folderIconFor = (folder) => {
  if (folder.system_key === 'all') return FileText;
  if (folder.system_key === 'governing') return Shield;
  if (folder.system_key === 'financial') return Database;
  if (folder.system_key === 'meeting-minutes') return Calendar;
  return Folder;
};

const folderKeyFor = (folder) => folder.system_key === 'all' ? 'All' : (folder.id || folder.system_key || folder.name);

const folderFromDb = (folder) => ({
  name: folder.name,
  icon: folderIconFor(folder),
  color: 'blue',
  children: [],
  dbId: folder.id,
  system_key: folder.system_key,
  category_filter: folder.category_filter || [],
  sort_order: folder.sort_order || 0,
});

function mapFolderToCategory(folderIdOrName) {
  if (!folderIdOrName) return 'other';
  return FOLDER_TO_CATEGORY[folderIdOrName] || 'other';
}

function getFolderCategories(folderIdOrName) {
  if (!folderIdOrName) return ['other'];
  return FOLDER_MULTI_CATEGORIES[folderIdOrName] || [mapFolderToCategory(folderIdOrName)];
}

function sortDocumentsForLibrary(docs) {
  return [...docs].sort((a, b) => {
    const aManual = a.display_order !== null && a.display_order !== undefined;
    const bManual = b.display_order !== null && b.display_order !== undefined;
    if (aManual && bManual) return a.display_order - b.display_order;
    if (aManual !== bManual) return aManual ? 1 : -1;
    return new Date(b.created_at || 0) - new Date(a.created_at || 0);
  });
}

// Enhanced Image Component — handles all image types with zoom controls
const TiffImage = ({ src, alt, className }) => {
  const [zoom, setZoom] = React.useState(1);
  const [error, setError] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const isTiff = src && (src.toLowerCase().includes('.tif') || alt?.toLowerCase().endsWith('.tiff'));
  if (isTiff) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
        <span className="material-symbols-outlined text-5xl text-slate-300">image</span>
        <p className="text-slate-500 text-sm">TIFF files cannot be previewed in the browser.</p>
        <a href={src} download className="px-4 py-2 bg-forest-600 text-white rounded-xl text-sm font-medium hover:bg-forest-700 transition-colors">Download File</a>
      </div>
    );
  }
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-center gap-2 px-4 py-2 border-b border-slate-200/60 bg-white">
        <button onClick={() => setZoom(z => Math.max(0.25, z - 0.25))} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-600 font-bold text-lg" title="Zoom out">−</button>
        <span className="text-xs text-slate-500 w-12 text-center">{Math.round(zoom * 100)}%</span>
        <button onClick={() => setZoom(z => Math.min(4, z + 0.25))} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-600 font-bold text-lg" title="Zoom in">+</button>
        <button onClick={() => setZoom(1)} className="ml-2 px-2 py-1 text-xs text-slate-500 hover:bg-slate-100 rounded-lg" title="Reset zoom">Reset</button>
      </div>
      <div className="flex-1 overflow-auto flex items-start justify-center p-4">
        {loading && <div className="absolute inset-0 flex items-center justify-center"><span className="text-slate-400 text-sm">Loading...</span></div>}
        {error ? (
          <div className="flex flex-col items-center justify-center gap-3 p-8">
            <span className="material-symbols-outlined text-4xl text-slate-300">broken_image</span>
            <p className="text-slate-500 text-sm">Image could not be loaded.</p>
          </div>
        ) : (
          <img src={src} alt={alt} onLoad={() => setLoading(false)} onError={() => { setLoading(false); setError(true); }}
            style={{ transform: `scale(${zoom})`, transformOrigin: 'top center', transition: 'transform 0.15s ease' }}
            className="max-w-full h-auto" />
        )}
      </div>
    </div>
  );
};

const isWordDocument = (document) => {
  const mime = (document?.mime_type || '').toLowerCase();
  const source = `${document?.file_name || ''} ${document?.name || ''}`.toLowerCase();
  return mime.includes('wordprocessingml') || /\.docx(?:\s|$)/.test(source);
};

const OfficeDocumentPreview = ({ document }) => {
  const [html, setHtml] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    let mounted = true;

    const loadPreview = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetch(document.content);
        if (!response.ok) throw new Error(`File request failed with ${response.status}.`);
        const arrayBuffer = await response.arrayBuffer();
        const mammoth = await import('mammoth');
        const result = await mammoth.convertToHtml({ arrayBuffer });
        if (!mounted) return;
        setHtml(result.value || '<p>No readable text was found in this Word document.</p>');
      } catch (previewError) {
        if (!mounted) return;
        setError(previewError?.message || 'This Word document could not be previewed.');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    if (document?.content) loadPreview();
    return () => {
      mounted = false;
    };
  }, [document?.content]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center bg-main-bg">
        <span className="material-symbols-outlined text-4xl text-secondary/60 animate-spin mb-4">progress_activity</span>
        <p className="text-sm font-bold text-primary-dark">Preparing Word preview...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center bg-main-bg">
        <span className="material-symbols-outlined text-5xl text-slate-300 mb-4">article</span>
        <h3 className="text-lg font-bold text-primary-dark mb-2">Preview needs download</h3>
        <p className="text-sm font-medium text-slate-500 max-w-md mb-6">{error}</p>
        <a
          href={document.content}
          download={document.name}
          className="bg-secondary text-white px-6 py-3 rounded-full font-bold hover:brightness-110 active:scale-95 transition-all"
        >
          Download Document
        </a>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto bg-main-bg p-4 sm:p-8">
      <article
        className="mx-auto max-w-4xl bg-white rounded-2xl border border-amber-100/40 px-6 sm:px-12 py-8 sm:py-10 text-primary-dark leading-relaxed warm-tactile-shadow
          [&_p]:mb-4 [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-5 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mb-4
          [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:mb-2 [&_table]:w-full [&_table]:border-collapse
          [&_td]:border [&_td]:border-slate-200 [&_td]:p-2 [&_th]:border [&_th]:border-slate-200 [&_th]:p-2"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
};

const DocumentManagement = ({ isDocumentFullScreen, setIsDocumentFullScreen, recentActivities, setRecentActivities, readOnly = false, showStatusDots = !readOnly }) => {
  const { user, isLoading: authLoading } = useAuth();
  const { hoa } = useHOA();
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatusText, setUploadStatusText] = useState('');
  const [deleteError, setDeleteError] = useState(null);
  const [pendingDeleteDocId, setPendingDeleteDocId] = useState(null);
  const [pendingDeleteFolderId, setPendingDeleteFolderId] = useState(null);
  const [revalidateMessage, setRevalidateMessage] = useState(null);
  const [folderStructure, setFolderStructure] = useState(DEFAULT_FOLDERS);
  const [draggingDocId, setDraggingDocId] = useState(null);
  const [dragOverDocId, setDragOverDocId] = useState(null);
  const [draggingFolderId, setDraggingFolderId] = useState(null);
  const [orderError, setOrderError] = useState(null);
  /*
    // Version stamp — bump this whenever default folders change to force-clear stale localStorage
    const FOLDER_STRUCTURE_VERSION = 2;
    const DEFAULT_FOLDERS = {
      'All': { name: 'All Documents', icon: FileText, color: 'blue', children: [] },
      'Governing': { name: 'Governing Documents', icon: Shield, color: 'red', children: [] },
      'Financial': { name: 'Financial Reports', icon: Database, color: 'green', children: [] },
      'Legal & Contracts': { name: 'Legal & Contracts', icon: Folder, color: 'purple', children: [] },
      'Meeting Minutes': { name: 'Meeting Minutes', icon: Calendar, color: 'slate', children: [] },
    };

    const savedVersion = parseInt(localStorage.getItem('folderStructureVersion') || '0', 10);
    if (savedVersion < FOLDER_STRUCTURE_VERSION) {
      localStorage.removeItem('folderStructure');
      localStorage.setItem('folderStructureVersion', String(FOLDER_STRUCTURE_VERSION));
      return DEFAULT_FOLDERS;
    }

    // Load folder structure from localStorage
    const saved = localStorage.getItem('folderStructure');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Restore icon references
        Object.keys(parsed).forEach(key => {
          if (parsed[key]) {
            switch (parsed[key].color) {
              case 'blue': parsed[key].icon = key === 'All' ? FileText : Folder; break;
              case 'red': parsed[key].icon = Shield; break;
              case 'green': parsed[key].icon = Database; break;
              case 'purple':
              case 'slate': parsed[key].icon = Calendar; break;
              default: parsed[key].icon = Folder; break;
            }
          }
        });
        return parsed;
      } catch (error) {
        console.error('Failed to load folder structure from localStorage:', error);
      }
    }
    return DEFAULT_FOLDERS;
  });
  */

  const [, setSidebarCollapsed] = useState(false);
  const [currentPanel, setCurrentPanel] = useState('folders');
  const [navigationStack, setNavigationStack] = useState([]);
  const [currentFolder, setCurrentFolder] = useState({ id: 'All', name: 'All Documents', children: [] });
  const [animatingOut, setAnimatingOut] = useState(false);
  const prevFolderRef = useRef(null);
  const [uploadModalFolder, setUploadModalFolder] = useState(null);
  const [showChatbot, setShowChatbot] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const queuedFileNames = useRef(new Set());
  const validationPanelRef = useRef(null);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [isPageFullScreen, setIsPageFullScreen] = useState(false);
  const [showDocumentMenu, setShowDocumentMenu] = useState(null);
  const [editingDocument, setEditingDocument] = useState(null);
  const [editingFolder, setEditingFolder] = useState(null);
  const [, setPdfSearchQuery] = useState('');

  // AI Search State
  const [aiQuery, setAiQuery] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [followUpQuery, setFollowUpQuery] = useState('');
  const [followUpLoading, setFollowUpLoading] = useState(false);
  const [showProvenance, setShowProvenance] = useState(false);
  const [conversation, setConversation] = useState([]);
  // feedbackGiven: maps turn index → true (thumbs up) | false (thumbs down)
  const [feedbackGiven, setFeedbackGiven] = useState({});

  // RAG ingestion validation state
  const [uploadedDocId, setUploadedDocId] = useState(null);
  const [uploadedDocName, setUploadedDocName] = useState('');
  const [validationReport, setValidationReport] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [showValidationPanel, setShowValidationPanel] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);

  // Level 4 stress test state
  const [level4Status, setLevel4Status] = useState('idle'); // idle | waiting_ingestion | generating | testing | ready | error
  const [level4Error, setLevel4Error] = useState(null);
  const [level4IngestionStatus, setLevel4IngestionStatus] = useState(null);
  const [level4Results, setLevel4Results] = useState(null);
  const canManageDocuments = !readOnly;
  const canReorderLibrary = canManageDocuments && user?.portalRole === 'admin';
  const resolvedHoaId = hoa?.id || user?.hoa_id;

  // Re-load documents once auth session is confirmed (avoids RLS returning 0 rows before session)
  useEffect(() => {
    if (!authLoading && user?.id && resolvedHoaId) {
      loadDocuments();
    }
    // loadDocuments updates folder structure; keep fetches keyed to auth and HOA readiness.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, resolvedHoaId, user?.id]);

  // Scroll validation panel into view when it appears after upload
  useEffect(() => {
    if (showValidationPanel && validationPanelRef.current) {
      setTimeout(() => {
        validationPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
    }
  }, [showValidationPanel]);

  // Close document menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDocumentMenu && !event.target.closest('.document-menu')) {
        setShowDocumentMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDocumentMenu]);

  // Handle ESC key functionality
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === 'Escape') {
        if (selectedDocument) {
          setSelectedDocument(null);
        } else if (isPageFullScreen) {
          setIsPageFullScreen(false);
          setShowChatbot(false);
        } else if (showChatbot) {
          setShowChatbot(false);
        } else if (currentPanel === 'documents' || (currentFolder && currentFolder.id !== 'All')) {
          navigateBack();
        } else if (showDocumentMenu) {
          setShowDocumentMenu(null);
        } else if (editingDocument) {
          setEditingDocument(null);
        } else if (editingFolder) {
          setEditingFolder(null);
        } else if (showUpload) {
          setShowUpload(false);
        } else if (showCreateFolder) {
          setShowCreateFolder(false);
          setNewFolderName('');
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  // navigateBack is stable via resetNavigation; keep this legacy key handler scoped to visible UI state.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDocument, isPageFullScreen, showChatbot, currentPanel, currentFolder, showDocumentMenu, editingDocument, editingFolder, showUpload, showCreateFolder]);

  useEffect(() => {
    if (prevFolderRef.current !== null && prevFolderRef.current !== currentFolder?.id) {
      setAnimatingOut(true);
      const t = setTimeout(() => setAnimatingOut(false), 180);
      return () => clearTimeout(t);
    }
    prevFolderRef.current = currentFolder?.id ?? null;
  }, [currentFolder]);

  const loadDocuments = async () => {
    if (authLoading || !user?.id || !resolvedHoaId) {
      setIsLoading(true);
      return;
    }

    setIsLoading(true);
    setLoadError(null);

    try {
      const [loadedDocuments, loadedFolders] = await Promise.all([
        documentStorage.getAllDocumentsWithFiles({ throwOnError: true, hoaId: resolvedHoaId }),
        documentStorage.getDocumentFolders({ hoaId: resolvedHoaId }),
      ]);
      const sortedDocuments = sortDocumentsForLibrary(loadedDocuments);
      setDocuments(sortedDocuments);

      const updatedFolderStructure = {};
      const foldersToRender = loadedFolders?.length ? loadedFolders : Object.entries(DEFAULT_FOLDERS).map(([key, folder]) => ({
        id: folder.dbId,
        name: folder.name,
        system_key: folder.system_key || (key === 'All' ? 'all' : key),
        category_filter: getFolderCategories(key),
        sort_order: folder.sort_order || 0,
      }));

      foldersToRender.forEach(folder => {
        const key = folderKeyFor(folder);
        updatedFolderStructure[key] = folderFromDb(folder);
      });
      if (!updatedFolderStructure.All) {
        updatedFolderStructure.All = DEFAULT_FOLDERS.All;
      }

      updatedFolderStructure.All.children = sortedDocuments.map(doc => doc.id);
      Object.keys(updatedFolderStructure).forEach(key => {
        if (key !== 'All') updatedFolderStructure[key].children = [];
      });

      sortedDocuments.forEach(doc => {
        const folderEntry = Object.entries(updatedFolderStructure).find(([, folder]) => folder.dbId && folder.dbId === doc.folder_id);
        if (folderEntry) {
          folderEntry[1].children.push(doc.id);
          return;
        }

        const fallbackEntry = Object.entries(updatedFolderStructure).find(([key, folder]) => {
          if (key === 'All') return false;
          return (folder.category_filter?.length ? folder.category_filter : getFolderCategories(key)).includes(doc.category || 'other');
        });
        if (fallbackEntry) fallbackEntry[1].children.push(doc.id);
      });

      setFolderStructure(updatedFolderStructure);
      if (currentFolder?.id && updatedFolderStructure[currentFolder.id]) {
        setCurrentFolder({ id: currentFolder.id, ...updatedFolderStructure[currentFolder.id] });
      }

    } catch (error) {
      console.error('Failed to load documents:', error);
      setLoadError(error?.message || 'Failed to load documents from database.');
      logEvent('documents_fetch_error', {
        portal_role: user.portalRole || 'unknown',
        source: 'DocumentManagement',
        read_only: readOnly,
        code: error?.code || null,
        message: error?.message || 'Failed to load documents from database.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Only reload after the tab has actually been hidden/dormant for a
    // stretch — a plain window "focus" event also fires on the very click
    // the user is making to interact with the page (e.g. clicking the edit
    // icon after the portal sat idle). Reloading then repaints the document
    // list mid-click, so the click lands on a stale/replaced node and is
    // lost — the user has to click a second time. Gate on visibilitychange
    // (reliable tab-dormant signal) instead of every focus event.
    const DORMANT_THRESHOLD_MS = 60 * 1000;
    let hiddenAt = null;

    const reloadOnWake = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        return;
      }
      if (document.visibilityState === 'visible') {
        const wasDormant = hiddenAt && (Date.now() - hiddenAt) > DORMANT_THRESHOLD_MS;
        hiddenAt = null;
        if (wasDormant) loadDocuments();
      }
    };
    const reloadOnOnline = () => loadDocuments();

    document.addEventListener('visibilitychange', reloadOnWake);
    window.addEventListener('online', reloadOnOnline);

    return () => {
      document.removeEventListener('visibilitychange', reloadOnWake);
      window.removeEventListener('online', reloadOnOnline);
    };
    // loadDocuments is a legacy local function; this wake handler should follow current auth/HOA state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, resolvedHoaId, user?.id]);
  const resetNavigation = useCallback(() => {
    setCurrentPanel('folders');
    setCurrentFolder({ id: 'All', name: 'All Documents', children: [] });
    setNavigationStack([]);
    setSidebarCollapsed(false);
  }, []);


  const navigateToFolder = (folder) => {
    setNavigationStack(prev => [...prev, { panel: currentPanel, folder: currentFolder }]);
    setCurrentFolder(folder);
    setCurrentPanel('documents');
    setSidebarCollapsed(true);
  };

  const navigateBack = useCallback(() => {
    // Always return to the main Document Library view - same as resetNavigation
    resetNavigation();
  }, [resetNavigation]);

  // Handle mouse back/forward buttons
  useEffect(() => {
    const handleMouseButton = (event) => {
      if (event.button === 3) { // Back button
        event.preventDefault();
        if (navigationStack.length > 0) {
          navigateBack();
        }
      } else if (event.button === 4) { // Forward button  
        event.preventDefault();
        // For forward functionality, we could implement a forward stack if needed
        // For now, just prevent default browser behavior
      }
    };

    document.addEventListener('mousedown', handleMouseButton);
    return () => {
      document.removeEventListener('mousedown', handleMouseButton);
    };
  }, [navigationStack, navigateBack]);

  const onDrop = useCallback((acceptedFiles) => {
    setUploadError(null);
    // Use a ref for synchronous dedup — prevents double-add when onDrop fires
    // twice for the same user action (react-dropzone click + change event race)
    const truly_new = acceptedFiles.filter(file => {
      if (queuedFileNames.current.has(file.name)) return false;
      queuedFileNames.current.add(file.name);
      return true;
    });
    if (truly_new.length === 0) return;
    const selectedFolder = folderStructure[uploadModalFolder?.id];
    const folderUpdates = selectedFolder && uploadModalFolder?.id !== 'All'
      ? { category: selectedFolder.category_filter?.[0] || 'other', folder_id: selectedFolder.dbId || null }
      : { category: 'other', folder_id: null };
    const newFiles = truly_new.map(file => ({
      file,
      name: file.name,
      size: (file.size / (1024 * 1024)).toFixed(2) + ' MB',
      type: file.type.includes('pdf') ? 'PDF' :
        (file.type.includes('image') || file.name.toLowerCase().endsWith('.tif') || file.name.toLowerCase().endsWith('.tiff')) ? 'TIFF' :
          'Document',
      category: folderUpdates.category,
      folderId: folderUpdates.folder_id,
      visibility: 'members',
      visibleToHomeowners: false,
      uploadDate: new Date().toISOString().split('T')[0],
      uploadedBy: user?.id || null
    }));
    setUploadFiles(prev => [...prev, ...newFiles]);
  }, [uploadModalFolder, user, folderStructure]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, noClick: false });

  const handleFileUpload = async () => {
    const hasInvalidCategory = !uploadModalFolder || uploadModalFolder.id === 'All';
    if (hasInvalidCategory) {
      setUploadError('Please select a destination folder before uploading.');
      return;
    }
    setUploadError(null);
    setIsUploading(true);
    try {
      // Process files sequentially so validation panel shows per-file
      const uploadedDocs = [];
      for (const fileData of uploadFiles) {
        const folderUpdates = buildDocumentFolderUpdates(uploadModalFolder?.id, fileData);
        const docData = {
          name: fileData.name,
          category: folderUpdates.category,
          folderId: folderUpdates.folder_id,
          size: fileData.size,
          type: fileData.type,
          uploadDate: fileData.uploadDate,
          uploadedBy: user?.id || null,
          status: 'published',
          downloads: 0,
          visibility: fileData.visibility,
          visibleToHomeowners: fileData.visibleToHomeowners === true,
          hoaId: resolvedHoaId
        };

        let extractionResult = null;
        const isPdf = fileData.file.type === 'application/pdf' || fileData.name.toLowerCase().endsWith('.pdf');
        if (isPdf) {
          setUploadStatusText(`Uploading ${fileData.name}…`);
          extractionResult = await extractTextFromPDF(fileData.file);
        }

        // PDFs are indexed below, after layout parsing — never chunk pdf-parse's
        // text first (it's garbage on scanned reports) or run two pipelines at once.
        const savedDocument = await documentStorage.saveDocument(docData, fileData.file, extractionResult, { skipIndexing: isPdf });

        let readerFailure = null;
        if (savedDocument && isPdf) {
          // Read the PDF with the AI reader (LlamaParse, then the Claude vision
          // backup). Never falls back silently: if both fail, the admin is told
          // why and pdf-parse's text is only used if it passes Level 2 below.
          const reading = await readDocumentWithAI(savedDocument.id, resolvedHoaId, setUploadStatusText);
          if (reading.ok) {
            extractionResult = reading.result;
            if (reading.notice) setRevalidateMessage(`"${fileData.name}": ${reading.notice}`);
          } else {
            readerFailure = reading;
            setRevalidateMessage(`"${fileData.name}": ${reading.message}`);
          }
        }

        if (savedDocument) {
          setUploadStatusText('Checking AI readiness…');
          const report = await runIngestionValidation(savedDocument, [fileData.file], extractionResult, { readerFailure });
          // PDFs are chunked only once their text has passed Level 2 — a scan's
          // pdf-parse text layer must never reach the index.
          if (isPdf && report?.levels?.[2]?.passed && extractionResult?.text && extractionResult.text.trim().length >= 50) {
            runIngestionPipeline(supabase, savedDocument.id, resolvedHoaId).then(null, (err) =>
              console.error('[DocumentManagement] Ingestion pipeline error:', err.message)
            );
          }
        }

        const docWithFile = await documentStorage.getDocumentWithFile(savedDocument.id);
        if (docWithFile) uploadedDocs.push(docWithFile);
      }

      setDocuments(prev => sortDocumentsForLibrary([...uploadedDocs, ...prev]));
      uploadedDocs.forEach(doc => {
        logEvent('document_upload', { document_id: doc.id, title: doc.name, category: doc.category });
      });

      await Promise.all(uploadedDocs.map(async (doc) => {
        try {
          await notificationService.notifyDocumentUploaded({ document: doc, actorUserId: user?.id || null });
        } catch (notificationError) {
          console.warn('Document notification failed:', notificationError?.message || notificationError);
        }
      }));

      await loadDocuments();

      if (setRecentActivities && uploadedDocs.length > 0) {
        const newActivities = uploadedDocs.map(doc => ({
          text: `Document uploaded: ${doc.name}`,
          time: 'Just now',
          type: 'document'
        }));
        setRecentActivities(prev => [...newActivities, ...prev].slice(0, 10));
      }

      setUploadFiles([]);
      queuedFileNames.current.clear();
      // Only auto-close if validation is not running
      if (uploadedDocs.length === 0) {
        setShowUpload(false);
      }
    } catch (error) {
      console.error('Failed to upload files:', error);
      setUploadError(error?.message || 'Upload failed. Check the browser console for details.');
    } finally {
      setIsUploading(false);
      setUploadStatusText('');
    }
  };

  const getFolderDocuments = (folderId) => {
    if (!folderId) return [];

    let docs = [];
    if (folderId === 'All') {
      docs = documents;
    } else {
      const folder = folderStructure[folderId];
      if (folder?.dbId) {
        docs = documents.filter(doc => doc.folder_id === folder.dbId);
      } else {
        const categories = folder?.category_filter?.length ? folder.category_filter : getFolderCategories(folderId);
        docs = documents.filter(doc => !doc.folder_id && categories.includes(doc.category));
      }
    }

    return sortDocumentsForLibrary(docs);
  };

  const getDocumentFolderKey = (doc) => {
    const byId = Object.entries(folderStructure).find(([, folder]) => folder.dbId && folder.dbId === doc.folder_id);
    if (byId) return byId[0];
    const byCategory = Object.entries(folderStructure).find(([key, folder]) => {
      if (key === 'All') return false;
      const categories = folder.category_filter?.length ? folder.category_filter : getFolderCategories(key);
      return categories.includes(doc.category || 'other');
    });
    return byCategory?.[0] || 'All';
  };

  const buildDocumentFolderUpdates = (folderKey, currentDoc = {}) => {
    const folder = folderStructure[folderKey];
    if (!folder || folderKey === 'All') {
      return { folder_id: null, category: currentDoc.category || 'other' };
    }

    const category = folder.category_filter?.[0] || currentDoc.category || 'other';
    return {
      folder_id: folder.dbId || null,
      category: category,
    };
  };

  // Create new folder
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;

    try {
      const createdFolder = await documentStorage.createDocumentFolder({
        hoaId: resolvedHoaId,
        name: newFolderName,
        createdBy: user?.id,
      });
      const key = folderKeyFor(createdFolder);
      const nextFolder = folderFromDb(createdFolder);

      setFolderStructure(prev => ({
        ...prev,
        [key]: nextFolder,
      }));
      setCurrentFolder({ id: key, ...nextFolder });
      setUploadModalFolder({ id: key, ...nextFolder });

      setUploadFiles(prev => prev.map(file => ({
        ...file,
        category: 'other',
        folderId: createdFolder.id,
      })));

      setNewFolderName('');
      setShowCreateFolder(false);
    } catch (error) {
      console.error('Failed to create folder:', error);
      setDeleteError(error?.message || 'Failed to create folder.');
    }
  };

  // Delete document
  const handleDeleteDocument = async (docId) => {
    try {
      const success = await documentStorage.deleteDocument(docId);
      if (!success) {
        setDeleteError('Could not delete document. You may not have permission, or a database error occurred.');
        return;
      }
      setDocuments(prev => prev.filter(doc => doc.id !== docId));

      // Update folder structure
      const updatedFolderStructure = { ...folderStructure };
      Object.keys(updatedFolderStructure).forEach(key => {
        updatedFolderStructure[key].children = updatedFolderStructure[key].children.filter(id => id !== docId);
      });
      setFolderStructure(updatedFolderStructure);
      setShowDocumentMenu(null);
      setEditingDocument(null);
      setPendingDeleteDocId(null);
    } catch (error) {
      console.error('Failed to delete document:', error);
      setDeleteError(error?.message || 'Failed to delete document.');
    }
  };

  // Update document
  const handleUpdateDocument = async (docId, updates) => {
    try {
      const updated = await documentStorage.updateDocumentMetadata(docId, updates);
      setDocuments(prev => sortDocumentsForLibrary(prev.map(doc =>
        doc.id === docId ? { ...doc, ...updates, ...(updates.title ? { name: updates.title } : {}) } : doc
      )));
      if (updated) await loadDocuments();

      setEditingDocument(null);
      setShowDocumentMenu(null);
    } catch (error) {
      console.error('Failed to update document:', error);
      setDeleteError(error?.message || 'Failed to update document.');
    }
  };

  // Update folder name
  const handleUpdateFolder = async (folderId, newName) => {
    if (!newName.trim() || folderId === 'All') return;

    try {
      const folder = folderStructure[folderId];
      if (!folder?.dbId) return;
      const updatedFolder = await documentStorage.updateDocumentFolder(folder.dbId, { name: newName });
      const nextFolder = folderFromDb(updatedFolder);
      setFolderStructure(prev => ({ ...prev, [folderId]: nextFolder }));

      // Update current folder if it's the one being edited
      if (currentFolder?.id === folderId) {
        setCurrentFolder({ id: folderId, ...nextFolder });
      }

      setEditingFolder(null);
    } catch (error) {
      console.error('Failed to update folder:', error);
      setDeleteError(error?.message || 'Failed to update folder.');
    }
  };

  // Delete folder
  const handleDeleteFolder = async (folderId) => {
    if (folderId === 'All') return;

    try {
      const docsInFolder = getFolderDocuments(folderId);
      if (docsInFolder.length > 0) {
        setDeleteError('This folder contains documents. Move them to another folder before deleting it. Only empty folders can be deleted.');
        setPendingDeleteFolderId(null);
        return;
      }

      const folder = folderStructure[folderId];
      if (folder?.dbId) {
        await documentStorage.deleteDocumentFolder(folder.dbId);
      }
      const updatedFolderStructure = { ...folderStructure };
      delete updatedFolderStructure[folderId];
      setFolderStructure(updatedFolderStructure);

      // Navigate back if we're currently viewing the deleted folder
      if (currentFolder?.id === folderId) {
        navigateBack();
      }

      setEditingFolder(null);
      setPendingDeleteFolderId(null);
    } catch (error) {
      console.error('Failed to delete folder:', error);
      setDeleteError(error?.message || 'Failed to delete folder.');
    }
  };

  // ── RAG VALIDATION HANDLERS ──────────────────────────────────

  const moveIdBeforeTarget = (ids, draggedId, targetId) => {
    if (!draggedId || !targetId || draggedId === targetId) return ids;
    const next = ids.filter(id => id !== draggedId);
    const targetIndex = next.indexOf(targetId);
    if (targetIndex === -1) return ids;
    next.splice(targetIndex, 0, draggedId);
    return next;
  };

  const handleDocumentDrop = async (targetDocId) => {
    if (!canReorderLibrary || !draggingDocId || draggingDocId === targetDocId) return;

    const visibleDocs = getFolderDocuments(currentFolder?.id || 'All');
    const orderedIds = moveIdBeforeTarget(visibleDocs.map(doc => doc.id), draggingDocId, targetDocId);
    const orderById = new Map(orderedIds.map((id, index) => [id, index]));

    setDraggingDocId(null);
    setDragOverDocId(null);
    setOrderError(null);
    setDocuments(prev => sortDocumentsForLibrary(prev.map(doc =>
      orderById.has(doc.id) ? { ...doc, display_order: orderById.get(doc.id) } : doc
    )));

    try {
      await documentStorage.updateDocumentOrder(orderedIds);
      await loadDocuments();
    } catch (error) {
      console.error('Failed to save document order:', error);
      setOrderError(error?.message || 'Document order could not be saved.');
      await loadDocuments();
    }
  };

  const handleFolderDrop = async (targetFolderId) => {
    if (!canReorderLibrary || !draggingFolderId || draggingFolderId === targetFolderId) return;

    const keys = Object.keys(folderStructure);
    const orderedKeys = moveIdBeforeTarget(keys, draggingFolderId, targetFolderId);
    const nextStructure = {};
    orderedKeys.forEach((key, index) => {
      nextStructure[key] = { ...folderStructure[key], sort_order: index * 10 };
    });

    setDraggingFolderId(null);
    setOrderError(null);
    setFolderStructure(nextStructure);

    try {
      const orderedDbIds = orderedKeys.map(key => folderStructure[key]?.dbId).filter(Boolean);
      await documentStorage.updateFolderOrder(orderedDbIds);
      await loadDocuments();
    } catch (error) {
      console.error('Failed to save folder order:', error);
      setOrderError(error?.message || 'Folder order could not be saved.');
      await loadDocuments();
    }
  };

  // Runs the automated 3-question stress test: waits for real chunking/embedding
  // to finish, has Haiku write questions grounded in this document's own text,
  // then answers each through the real production RAG path (runRAGQuery — the
  // same function every homeowner query uses). No pass/fail verdict is computed
  // here; the admin reads the real answers + sources and decides.
  const runLevel4StressTest = async (docId) => {
    setLevel4Status('waiting_ingestion');
    setLevel4Error(null);
    setLevel4Results(null);

    const readiness = await waitForIngestionReady(docId, (status) => setLevel4IngestionStatus(status));
    if (!readiness.ready) {
      setLevel4Status('error');
      setLevel4Error(readiness.error || 'Chunking/embedding did not finish in time.');
      return;
    }

    setLevel4Status('generating');
    let questions;
    try {
      questions = await generateStressTestQuestions(supabase, docId, resolvedHoaId);
    } catch (err) {
      setLevel4Status('error');
      setLevel4Error(err.message);
      return;
    }

    setLevel4Status('testing');
    try {
      const results = await runStressTest(supabase, resolvedHoaId, user.id, user.portalRole, docId, questions);
      setLevel4Results(results);
      setLevel4Status('ready');
    } catch (err) {
      setLevel4Status('error');
      setLevel4Error(err.message);
    }
  };

  const handleLevel4Approve = async () => {
    if (!uploadedDocId) {
      console.error('Level 4: no document ID in state');
      return;
    }

    try {
      await documentsService.updateDocumentStatus(uploadedDocId, 'live');
      setDocuments(prev => prev.map(doc =>
        doc.id === uploadedDocId ? { ...doc, ai_status: 'live' } : doc
      ));
      handleSkipLevel4();
    } catch (err) {
      console.error('Level 4 approval error:', err);
      setLevel4Status('error');
      setLevel4Error(err.message);
    }
  };

  const handleLevel4Reject = async () => {
    if (uploadedDocId) {
      await documentsService.updateDocumentStatus(uploadedDocId, 'pending');
      // Update the document in local state immediately
      setDocuments(prev => prev.map(doc =>
        doc.id === uploadedDocId
          ? { ...doc, ai_status: 'pending' }
          : doc
      ));
    }
    handleSkipLevel4();
  };

  const handleSkipLevel4 = () => {
    // Reset all validation state
    setShowValidationPanel(false);
    setValidationReport(null);
    setIsValidating(false);
    setUploadedDocId(null);
    setUploadedDocName('');
    setUploadComplete(false);
    setLevel4Status('idle');
    setLevel4Error(null);
    setLevel4IngestionStatus(null);
    setLevel4Results(null);
    setUploadFiles([]);
    queuedFileNames.current.clear();
    setShowUpload(false);
    // Refresh documents list to show updated status dots (commented out to avoid reverting status)
    // if (typeof loadDocuments === 'function') loadDocuments();
  };

  // options.readerFailure: the AI reader couldn't read this PDF, so the text
  // being validated is pdf-parse's text layer. If that fails Level 2, the admin
  // is told the real cause (e.g. out of credits), not just "bad scan".
  // Returns the validation report (or null on error) so callers can decide
  // whether the text is fit to chunk.
  const runIngestionValidation = async (document, files, precomputedExtractionResult = null, options = {}) => {
    const file = Array.isArray(files) ? files[0] : files; // handle both scenarios

    setIsValidating(true);
    setShowValidationPanel(true);
    setValidationReport(null);
    setUploadedDocId(document.id);
    setUploadedDocName(document.name || document.title || '');

    try {
      // Extract text from file
      const extractionResult = precomputedExtractionResult || await documentIntelligence.extractText(
        file,
        () => { } // progress callback — silent for now
      );

      // Store extraction results
      await documentsService.updateDocumentExtraction(document.id, {
        extracted_text: extractionResult?.text || '',
        text_extracted: !!(extractionResult?.text),
        extraction_quality: extractionResult?.quality || 'failed',
        extraction_method: extractionResult?.method || 'unknown',
        word_count: extractionResult?.wordCount || 0,
        page_count: extractionResult?.pageCount || 0,
        ai_status: (!extractionResult?.text ||
          extractionResult?.quality === 'failed')
          ? 'failed' : 'validating',
      });

      // Run levels 1-4
      const report = await ingestionValidator.runAutomaticValidation(
        document,
        extractionResult
      );
      if (options.readerFailure && !report.levels?.[2]?.passed) {
        const failedLevel = report.levels?.[2] ? 2 : 1;
        report.levels[failedLevel] = {
          ...report.levels[failedLevel],
          message: `✗ ${options.readerFailure.message} This PDF's own text layer isn't usable (it looks like a scan), so the document can't be made AI-searchable yet. It has been saved — use Re-validate once the reader is available.`,
        };
      }

      // Write ai_status back based on validation outcome
      // 'validating' was set optimistically before validation — now resolve it
      const finalStatus = report.ready_for_level_5
        ? 'validating'   // passed all 4, awaiting Level 5 stress test review
        : 'failed';      // failed Level 1, 2, 3, or 4

      await documentsService.updateDocumentExtraction(document.id, {
        ai_status: finalStatus,
        validation_level: report.highest_level_passed,
      });

      // Update local state so the dot reflects the result immediately
      setDocuments(prev => prev.map(doc =>
        doc.id === document.id ? { ...doc, ai_status: finalStatus } : doc
      ));

      setValidationReport(report);
      setUploadComplete(true);

      if (report.ready_for_level_5) {
        runLevel4StressTest(document.id);
      }
      return report;

    } catch (err) {
      console.error('Ingestion validation error:', err);
      setValidationReport({
        ready_for_level_5: false,
        highest_level_passed: 0,
        levels: {
          1: {
            level: 1,
            name: 'Existence Test',
            passed: false,
            message: `Extraction error: ${err.message}`,
          },
        },
      });
      setUploadComplete(true);
      return null;
    } finally {
      setIsValidating(false);
    }
  };

  const handleRevalidate = async (doc) => {
    let fileToProcess = doc.file;

    // Try to reconstruct the file from base64 or blob content if doc.file is missing
    if (!fileToProcess && doc.content && typeof doc.content === 'string' && (doc.content.startsWith('data:') || doc.content.startsWith('blob:'))) {
      try {
        const res = await fetch(doc.content);
        const blob = await res.blob();
        const mimeType = blob.type || 'application/octet-stream';
        fileToProcess = new File([blob], doc.name || 'document', { type: mimeType });
      } catch (e) {
        console.error('Failed to reconstruct file from content', e);
      }
    }

    // If we have a file, run the full validation flow from the ground up
    if (fileToProcess) {
      if (typeof setShowUpload === 'function') setShowUpload(true);
      await runIngestionValidation(doc, [fileToProcess]);
      return;
    }

    // Stored PDF: re-read it with layout-aware parsing. This is how scanned
    // reports that failed Level 2 under the old pdf-parse extractor get fixed
    // without deleting and re-uploading them.
    if (doc.file_url && isPdfDocument(doc)) {
      setIsValidating(true);
      setShowValidationPanel(true);
      setValidationReport(null);
      setUploadedDocId(doc.id);
      setUploadedDocName(doc.name || doc.title || '');
      if (typeof setShowUpload === 'function') setShowUpload(true);

      const reading = await readDocumentWithAI(doc.id, resolvedHoaId, setUploadStatusText);
      setUploadStatusText('');
      if (!reading.ok) {
        // Don't fall back to the stored text silently: tell the admin why.
        setIsValidating(false);
        setShowValidationPanel(false);
        setRevalidateMessage(`"${doc.name || doc.title}": ${reading.message} Nothing was changed — try Re-validate again once the reader is available.`);
        return;
      }
      if (reading.notice) setRevalidateMessage(`"${doc.name || doc.title}": ${reading.notice}`);
      // Old chunks still satisfy the stress test's readiness check
      // (is_indexed + chunk_count), so clear them before re-chunking or the
      // test could run against the previous extraction.
      await documentsService.updateDocumentExtraction(doc.id, { is_indexed: false, chunk_count: 0 });
      const report = await runIngestionValidation(doc, [], reading.result);
      if (report?.levels?.[2]?.passed) {
        runIngestionPipeline(supabase, doc.id, resolvedHoaId).then(null, (err) =>
          console.error('[DocumentManagement] Ingestion pipeline error:', err.message)
        );
      }
      return;
    }

    // Use stored extraction data — original file not available
    const storedText = doc.extracted_text || '';
    const wordCount = doc.word_count ||
      storedText.split(/\s+/).filter(Boolean).length;

    if (!storedText || wordCount < 10) {
      // No stored text — cannot re-validate without re-upload
      setRevalidateMessage('This document has no extracted text. Please delete it and re-upload to run full validation.');
      return;
    }

    // Build synthetic extraction result from stored data
    const syntheticExtraction = {
      text: storedText,
      wordCount,
      pageCount: doc.page_count || null,
      quality: doc.extraction_quality || 'partial',
      method: doc.extraction_method || 'stored',
    };

    // Run validation levels 1-3
    setIsValidating(true);
    setShowValidationPanel(true);
    setValidationReport(null);
    setUploadedDocId(doc.id);
    setUploadedDocName(doc.name || doc.title || '');

    try {
      const report = await ingestionValidator.runAutomaticValidation(
        doc,
        syntheticExtraction
      );
      setValidationReport(report);
      setUploadComplete(true);

      if (report.ready_for_level_5) {
        runLevel4StressTest(doc.id);
      }
    } catch (err) {
      console.error('Re-validate error:', err);
    } finally {
      setIsValidating(false);
    }

    // Show the validation panel in a modal
    // If the upload modal is used for this, open it:
    if (typeof setShowUpload === 'function') setShowUpload(true);
  };


  const ValidationPanel = () => {
    if (!showValidationPanel) return null;

    const levels = [
      { num: 1, name: 'Existence' },
      { num: 2, name: 'OCR Quality' },
      { num: 3, name: 'Content' },
      { num: 4, name: 'Coverage' },
      { num: 5, name: 'Stress Test' },
    ];

    return (
      <div ref={validationPanelRef} className="mt-4 bg-cream rounded-xl p-4 border border-forest-200">
        <h4 className="font-display font-bold text-slate-800 text-sm mb-0.5">
          AI Readiness Verification
        </h4>
        {uploadedDocName && (
          <p className="text-xs text-slate-400 mb-3 truncate">{uploadedDocName}</p>
        )}

        {isValidating && (
          <p className="text-xs text-forest-600 mb-2 animate-pulse">
            Extracting document text...
          </p>
        )}

        {levels.map(({ num, name }) => {
          const result = validationReport?.levels?.[num];
          const isStressTestLevel = num === 5;
          const isReady = isStressTestLevel && validationReport?.ready_for_level_5;
          const isPending = !result && !isReady;

          return (
            <div key={num} className="flex items-start gap-3 mb-2">
              <div className="mt-0.5 shrink-0 w-5 h-5 rounded-full flex items-center justify-center">
                {isPending && (
                  <div className="w-4 h-4 rounded-full bg-slate-200" />
                )}
                {result?.passed === true && (
                  <div className="w-5 h-5 rounded-full bg-forest-600 flex items-center justify-center">
                    <span className="text-white text-xs font-bold">✓</span>
                  </div>
                )}
                {result?.passed === false && (
                  <div className="w-5 h-5 rounded-full bg-red-400 flex items-center justify-center">
                    <span className="text-white text-xs font-bold">✗</span>
                  </div>
                )}
                {isReady && !result && (
                  <div className="w-5 h-5 rounded-full bg-amber-400 flex items-center justify-center">
                    <span className="text-white text-xs font-bold">!</span>
                  </div>
                )}
              </div>

              <div className="flex-1">
                <div className="text-xs font-semibold text-slate-700">
                  Level {num} — {name}
                </div>

                {result && (
                  <div className={`text-xs mt-0.5 ${result.passed ? 'text-forest-600' : 'text-red-500'
                    }`}>
                    {result.message}
                  </div>
                )}

                {isReady && !result && (
                  <div className="mt-2">
                    {level4Status === 'waiting_ingestion' && (
                      <p className="text-xs text-slate-500 animate-pulse">
                        Waiting for chunking &amp; embedding to finish
                        {level4IngestionStatus
                          ? ` (${level4IngestionStatus.embeddedCount}/${level4IngestionStatus.chunkCount} chunks embedded)...`
                          : '...'}
                      </p>
                    )}
                    {level4Status === 'generating' && (
                      <p className="text-xs text-slate-500 animate-pulse">
                        Writing 3 test questions from this document...
                      </p>
                    )}
                    {level4Status === 'testing' && (
                      <p className="text-xs text-slate-500 animate-pulse">
                        Running each question through AI Search...
                      </p>
                    )}
                    {level4Status === 'error' && (
                      <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                        <p className="font-semibold mb-1">Could not verify this document is RAG-ready:</p>
                        <p>{level4Error}</p>
                        <button
                          type="button"
                          onClick={() => runLevel4StressTest(uploadedDocId)}
                          className="mt-2 text-red-700 underline font-semibold"
                        >
                          Retry
                        </button>
                      </div>
                    )}
                    {level4Status === 'ready' && level4Results && (
                      <div className="space-y-3">
                        <p className="text-xs text-slate-500">
                          Read each answer below. Only approve if they look right —
                          this is your call, not the AI's.
                        </p>
                        {level4Results.map((r, i) => (
                          <div key={i} className="bg-white border border-slate-200 rounded-lg p-3">
                            <div className="flex items-center gap-2 mb-1.5">
                              <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${
                                r.level === 'simple' ? 'bg-slate-100 text-slate-600' :
                                r.level === 'moderate' ? 'bg-amber-100 text-amber-700' :
                                'bg-purple-100 text-purple-700'
                              }`}>
                                {r.level}
                              </span>
                              {r.flag && (
                                <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                                  needs review
                                </span>
                              )}
                            </div>
                            <p className="text-xs font-semibold text-slate-700 mb-1">{r.question}</p>
                            {r.error ? (
                              <p className="text-xs text-red-600">Query failed: {r.error}</p>
                            ) : (
                              <>
                                <p className="text-xs text-slate-600 whitespace-pre-line mb-1.5">{r.answer}</p>
                                {r.flag && (
                                  <p className="text-xs text-red-600 italic mb-1.5">⚠ {r.flag}</p>
                                )}
                                {r.citations.length > 0 && (
                                  <p className="text-[10px] text-slate-400">
                                    Sources: {r.citations.map(c => c.document_name).join(', ')}
                                  </p>
                                )}
                              </>
                            )}
                          </div>
                        ))}
                        <div className="flex gap-2 pt-1">
                          <button
                            type="button"
                            onClick={handleLevel4Approve}
                            className="flex-1 text-xs font-semibold bg-forest-700 text-white px-3 py-2 rounded-lg hover:bg-forest-600 transition-colors"
                          >
                            Looks right — make AI-searchable
                          </button>
                          <button
                            type="button"
                            onClick={handleLevel4Reject}
                            className="flex-1 text-xs font-semibold bg-white border border-slate-200 text-slate-600 px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors"
                          >
                            Something's wrong — keep unpublished
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {uploadComplete && (
          <div className="mt-3 pt-3 border-t border-forest-200">
            {validationReport?.ready_for_level_5 ? (
              <p className="text-xs text-slate-500 italic">
                Complete the stress test review above to make this
                document AI-searchable. You can skip and verify later.
              </p>
            ) : (
              <p className="text-xs text-slate-500 italic">
                Document stored. AI search unavailable for this file.
              </p>
            )}
            <button
              type="button"
              onClick={handleSkipLevel4}
              className="text-xs text-slate-400 hover:text-slate-600 underline mt-1 block"
            >
              {validationReport?.ready_for_level_5
                ? 'Skip for now — document will be stored but not AI-searchable'
                : 'Close'}
            </button>
          </div>
        )}
      </div>
    );
  };

  const handleAiSearch = async () => {
    if (!aiQuery.trim()) return;
    setAiLoading(true);

    try {
      // Clear UI display for new top-level question, but keep conversation
      // memory intact — clearSession only happens on explicit "New Search"
      setConversation([]);

      // runRAGQuery logs the rag_query event itself (once, with outcome metadata).
      const result = await runRAGQuery(supabase, resolvedHoaId, user.id, user.portalRole, aiQuery);

      // Start new conversation with this first turn
      setConversation([{ query: aiQuery, result }]);
      setAiQuery('');

    } catch (err) {
      setConversation([{
        query: aiQuery,
        result: {
          answer: 'Search unavailable. Please try again.',
          confidence: { level: 'none', emoji: '⚪', label: 'Error' },
          citations: [],
        }
      }]);
    } finally {
      setAiLoading(false);
    }
  };

  const handleFollowUp = async () => {
    if (!followUpQuery.trim() || followUpLoading) return;
    setFollowUpLoading(true);

    const questionText = followUpQuery;
    setFollowUpQuery('');

    try {
      // ragEngine already maintains conversation history
      // via conversationManager — just query normally
      const result = await runRAGQuery(supabase, resolvedHoaId, user.id, user.portalRole, questionText);

      // Append new turn to conversation
      setConversation(prev => [
        ...prev,
        { query: questionText, result }
      ]);

    } catch (err) {
      setConversation(prev => [
        ...prev,
        {
          query: questionText,
          result: {
            answer: 'Unable to answer. Please try again.',
            confidence: {
              level: 'none', emoji: '⚪',
              label: 'Error'
            },
            citations: [],
          }
        }
      ]);
    } finally {
      setFollowUpLoading(false);
    }
  };

  const handleClearConversation = () => {
    setConversation([]);
    setAiQuery('');
    setFollowUpQuery('');
    // Clear conversation history from conversationManager
    try {
      const currentUser = JSON.parse(
        localStorage.getItem('currentUser') ||
        localStorage.getItem('authUser') ||
        '{"id":"user_1","role":"admin","name":"Administrator"}'
      );
      conversationManager.clearSession(currentUser.id || 'user_1');
    } catch (e) { }
    setFeedbackGiven({});
  };

  const handleRating = async (turnIndex, rating) => {
    // Optimistic UI update
    setFeedbackGiven(prev => ({ ...prev, [turnIndex]: rating }));

    const turn = conversation[turnIndex];
    if (!turn) return;

    // Rates the exact rag_query_log row runRAGQuery inserted (by id), and logs
    // a rag_feedback activity event.
    try {
      await rateAnswer({
        logId: turn.result?.logId,
        rating,
        query: turn.query,
        answerLength: (turn.result?.answer || '').length,
      });
    } catch (e) {
      console.warn('AI answer rating could not be saved:', e?.message || e);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            <FileText className="w-7 h-7" />
            Document Library
          </h2>
          <p className="text-gray-600">Manage and organize your HOA documents</p>
        </div>
        {!readOnly && (
          <div className="flex items-center gap-2">
            <button onClick={() => setShowCreateFolder(true)} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 hover:border-forest-300 text-slate-700 rounded-xl transition-all hover:shadow-sm">
              <Plus className="w-4 h-4" /> New Folder
            </button>
            <button onClick={() => {
              setUploadModalFolder(null);
              setShowValidationPanel(false);
              setValidationReport(null);
              setUploadComplete(false);
              setUploadError(null);
              setShowUpload(true);
            }} className="flex items-center gap-2 px-4 py-2 bg-forest-700 hover:bg-forest-600 text-white rounded-xl transition-all hover:shadow-lg">
              <Upload className="w-4 h-4" /> Upload
            </button>
          </div>
        )}
      </div>

      {/* Admin-only: surfaces chunks silently missing vector embeddings */}
      {!readOnly && user?.portalRole === 'admin' && resolvedHoaId && (
        <EmbeddingHealthTile hoaId={resolvedHoaId} />
      )}

      {/* ── AI DOCUMENT SEARCH ─────────────────────────────── */}
      <div className="mb-6">

        {/* Initial search input — only show when no active conversation */}
        {conversation.length === 0 && (
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 
                -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Ask a question about your HOA documents..."
                value={aiQuery}
                onChange={e => setAiQuery(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleAiSearch();
                  if (e.key === 'Escape') handleClearConversation();
                }}
                className="w-full pl-9 pr-4 py-2.5 text-sm border 
                  border-slate-200 rounded-xl focus:outline-none 
                  focus:border-forest-500 bg-white"
              />
            </div>
            <button
              type="button"
              onClick={handleAiSearch}
              disabled={aiLoading || !aiQuery.trim()}
              className="px-4 py-2.5 bg-forest-700 text-white text-sm 
                font-medium rounded-xl hover:bg-forest-600 
                disabled:opacity-50 disabled:cursor-not-allowed 
                transition-colors flex items-center gap-2"
            >
              {aiLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 
                    border-t-white rounded-full animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Ask AI
                </>
              )}
            </button>
          </div>
        )}

        {/* Conversation thread */}
        {conversation.length > 0 && (
          <div className="border border-slate-200 rounded-xl 
            overflow-hidden">

            {/* Conversation header */}
            <div className="px-4 py-2.5 bg-slate-50 border-b 
              border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs 
                text-slate-500">
                <Sparkles className="w-3.5 h-3.5 text-forest-600" />
                AI Document Search
                <span className="text-slate-300">·</span>
                {conversation.length} turn{conversation.length !== 1
                  ? 's' : ''}
              </div>
              <button
                type="button"
                onClick={handleClearConversation}
                className="text-xs font-semibold px-3 py-1.5 bg-white border border-slate-200 
                  text-slate-700 hover:bg-slate-50 hover:text-slate-900 rounded-lg 
                  shadow-sm transition-all flex items-center gap-1.5"
              >
                ✕ New search
              </button>
            </div>

            {/* Conversation turns */}
            <div className="divide-y divide-slate-100 max-h-[60vh] 
              overflow-y-auto">
              {conversation.map((turn, index) => (
                <div key={index} className="p-4">

                  {/* User question */}
                  <div className="flex items-start gap-2 mb-3">
                    <div className="w-6 h-6 rounded-full bg-forest-100 
                      flex items-center justify-center shrink-0 mt-0.5">
                      <span className="text-xs text-forest-700 
                        font-medium">Q</span>
                    </div>
                    <p className="text-sm font-medium text-slate-700 
                      pt-0.5">
                      {turn.query}
                    </p>
                  </div>

                  {/* AI answer */}
                  <div className="flex items-start gap-2">
                    <div className="w-6 h-6 rounded-full bg-slate-100 
                      flex items-center justify-center shrink-0 mt-0.5">
                      <span className="text-base leading-none">
                        {turn.result.confidence?.emoji || '⚪'}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-800
                        leading-relaxed whitespace-pre-line">
                        {turn.result.answer}
                      </p>

                      {/* Thumbs feedback */}
                      {turn.result.answer && !turn.result.answer.includes('Search unavailable') && (
                        <div className="flex items-center justify-end gap-2 mt-2">
                          <span className="text-xs text-slate-400">Was this helpful?</span>
                          <button
                            type="button"
                            onClick={() => handleRating(index, true)}
                            className={`p-1 rounded transition-colors ${feedbackGiven[index] === true ? 'text-forest-600' : 'text-slate-300 hover:text-forest-500'}`}
                            title="Helpful"
                          >
                            <ThumbsUp className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRating(index, false)}
                            className={`p-1 rounded transition-colors ${feedbackGiven[index] === false ? 'text-forest-600' : 'text-slate-300 hover:text-slate-500'}`}
                            title="Not helpful"
                          >
                            <ThumbsDown className="w-4 h-4" />
                          </button>
                        </div>
                      )}

                      {/* Temporal warning */}
                      {turn.result.temporal_warning && (
                        <div className="mt-2 text-xs text-amber-700 
                          bg-amber-50 border border-amber-200 
                          rounded-lg px-3 py-2">
                          ⏰ {turn.result.temporal_warning}
                        </div>
                      )}

                      {/* Confidence + sources — collapsed by default */}
                      {turn.result.citations?.length > 0 && (
                        <div className="mt-2 pt-2 border-t 
                          border-slate-100">
                          <p className="text-xs text-slate-400 mb-1.5">
                            {turn.result.confidence?.label} · Sources
                          </p>
                          <div className="space-y-1">
                            {turn.result.citations
                              .slice(0, 2)
                              .map((cite, i) => (
                                <div key={i} className="text-xs 
                                text-slate-500 bg-slate-50 rounded-lg 
                                px-3 py-1.5">
                                  <span className="font-medium 
                                  text-slate-600">
                                    {cite.document_name}
                                  </span>
                                  <span className="mx-1.5 
                                  text-slate-300">·</span>
                                  <span className="italic">
                                    {cite.excerpt?.substring(0, 120)}...
                                  </span>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}

                      {/* Provenance — last turn only */}
                      {index === conversation.length - 1 &&
                        turn.result.provenance && (
                          <div className="mt-2">
                            <button
                              type="button"
                              onClick={() =>
                                setShowProvenance(!showProvenance)}
                              className="text-xs text-slate-400 
                              hover:text-slate-600 
                              flex items-center gap-1"
                            >
                              {showProvenance ? '▼' : '▶'}
                              Search provenance
                            </button>
                            {showProvenance && (
                              <div className="mt-1.5 text-xs 
                              text-slate-400 space-y-0.5 
                              bg-slate-50 rounded-lg p-3">
                                <div>Documents searched: {
                                  turn.result.provenance
                                    .documents_searched
                                }</div>
                                <div>Passages found: {
                                  turn.result.provenance.passages_found
                                }</div>
                                <div>Sources: {
                                  turn.result.provenance.sources
                                    ?.join(', ') || 'none'
                                }</div>
                                <div>AI called: {
                                  turn.result.provenance.api_called
                                    ? '✓' : '✗'
                                }</div>
                              </div>
                            )}
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Follow-up input */}
            <div className="px-4 py-3 bg-slate-50 border-t 
              border-slate-200">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Ask a follow-up question..."
                  value={followUpQuery}
                  onChange={e => setFollowUpQuery(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleFollowUp();
                    if (e.key === 'Escape') handleClearConversation();
                  }}
                  className="flex-1 text-sm border border-slate-200 
                    rounded-xl px-3 py-2 focus:outline-none 
                    focus:border-forest-500 bg-white"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={handleFollowUp}
                  disabled={followUpLoading || !followUpQuery.trim()}
                  className="px-4 py-2 bg-forest-700 text-white 
                    text-sm font-medium rounded-xl 
                    hover:bg-forest-600 disabled:opacity-50 
                    disabled:cursor-not-allowed transition-colors 
                    flex items-center gap-2 shrink-0"
                >
                  {followUpLoading ? (
                    <div className="w-4 h-4 border-2 
                      border-white/30 border-t-white rounded-full 
                      animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                  Ask
                </button>
              </div>
              <p className="text-xs text-slate-400 mt-1.5">
                AI remembers this conversation context.
                Click "New search" to start fresh.
              </p>
            </div>
          </div>
        )}
      </div>
      {/* ── END AI SEARCH ────────────────────────────────────── */}

      {/* Two-Panel Layout */}
      <div className="flex flex-col md:flex-row gap-6 min-h-[500px]">
        {/* Mobile Folder Select */}
        <div className="block md:hidden">
          <select
            className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:border-forest-500 focus:ring-2 focus:ring-forest-100 font-medium text-slate-700 shadow-sm"
            value={currentFolder?.id || 'All'}
            onChange={(e) => {
              const key = e.target.value;
              if (key === 'All') {
                setCurrentFolder({ id: 'All', name: 'All Documents', children: [] });
              } else {
                const category = folderStructure[key];
                if (category) {
                  navigateToFolder({ id: key, ...category });
                }
              }
              setSelectedDocument(null);
            }}
          >
            <option value="All">All Documents</option>
            {Object.entries(folderStructure).map(([key, category]) => {
              const count = getFolderDocuments(key).length;
              return <option key={key} value={key}>{category.name} ({count})</option>;
            })}
          </select>
        </div>

        {true && (
          <div className="hidden md:block w-56 shrink-0 bg-white rounded-2xl border border-slate-200/60 shadow-sm p-4">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Folders</h3>
            <div className="space-y-1">
              {Object.entries(folderStructure).map(([key, category]) => {
                const count = getFolderDocuments(key).length;
                const isActive = currentFolder?.id === key;
                return (
                  <button
                    key={key}
                    draggable={canReorderLibrary}
                    onDragStart={(e) => { if (canReorderLibrary) { e.stopPropagation(); setDraggingFolderId(key); e.dataTransfer.effectAllowed = 'move'; } }}
                    onDragOver={(e) => { if (canReorderLibrary) e.preventDefault(); }}
                    onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleFolderDrop(key); }}
                    onDragEnd={() => setDraggingFolderId(null)}
                    onClick={() => { navigateToFolder({ id: key, ...category }); setSelectedDocument(null); }}
                    className={`group w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-left text-sm transition-all ${isActive ? 'bg-forest-50 text-forest-700 font-semibold' : 'text-slate-600 hover:bg-cream'}`}>
                    {canReorderLibrary && (
                      <span className="material-symbols-outlined text-[18px] text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity">drag_handle</span>
                    )}
                    <Folder className={`w-4 h-4 ${isActive ? 'text-forest-600' : 'text-slate-400'}`} />
                    <span className="flex-1 truncate">{category.name}</span>
                    <span className="text-xs text-slate-400">{count}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex-1 bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden flex flex-col">
          <div className="px-5 py-4 border-b border-slate-100">
            {currentFolder ? (
              <div className="flex items-center gap-2 text-sm">
                <button onClick={() => setCurrentFolder({ id: 'All', name: 'All Documents', children: [] })} className="text-slate-500 hover:text-forest-600">All Documents</button>
                {currentFolder.id !== 'All' && (<><span className="text-slate-300">/</span><span className="text-slate-800 font-medium">{currentFolder.name}</span></>)}
              </div>
            ) : (
              <p className="text-sm text-slate-500">Select a folder to view documents</p>
            )}
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {loadError ? (
              <div className="text-center py-12">
                <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
                <h3 className="text-slate-700 text-base font-semibold mb-1">Could not load documents</h3>
                <p className="text-sm text-red-500 mb-3">{loadError}</p>
                <button onClick={loadDocuments} className="text-sm px-4 py-2 bg-forest-700 text-white rounded-xl hover:bg-forest-600">Retry</button>
              </div>
            ) : orderError ? (
              <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-amber-800">Order was not saved</p>
                  <p className="text-xs text-amber-700 mt-0.5">{orderError}</p>
                </div>
                <button onClick={() => setOrderError(null)} className="text-amber-500 hover:text-amber-700"><X className="w-4 h-4" /></button>
              </div>
            ) : isLoading ? (
              <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-forest-600 mx-auto mb-4" /><p className="text-slate-500 text-sm">Loading…</p></div>
            ) : currentFolder ? (
              getFolderDocuments(currentFolder.id).length > 0 ? (
                <div className="space-y-2">
                  {getFolderDocuments(currentFolder.id).map(doc => {
                    return (
                      <React.Fragment key={doc.id}>
                        {canReorderLibrary && draggingDocId && dragOverDocId === doc.id && draggingDocId !== doc.id && (
                          <div
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleDocumentDrop(doc.id); }}
                            className="h-[68px] rounded-2xl border-2 border-dashed border-secondary/60 bg-secondary/10 transition-all duration-200 ease-out"
                          />
                        )}
                      <div
                        draggable={canReorderLibrary}
                        onDragStart={(e) => { if (canReorderLibrary) { e.stopPropagation(); setDraggingDocId(doc.id); setDragOverDocId(null); e.dataTransfer.effectAllowed = 'move'; } }}
                        onDragEnter={() => { if (canReorderLibrary && draggingDocId && draggingDocId !== doc.id) setDragOverDocId(doc.id); }}
                        onDragOver={(e) => { if (canReorderLibrary) e.preventDefault(); }}
                        onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleDocumentDrop(doc.id); }}
                        onDragEnd={() => { setDraggingDocId(null); setDragOverDocId(null); }}
                        onClick={() => {
                          setSelectedDocument(doc);
                          logEvent('document_view', {
                            document_id: doc.id,
                            title: doc.name,
                            category: doc.category,
                            open_mode: 'portal_preview',
                          });
                        }}
                        role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') { setSelectedDocument(doc); logEvent('document_view', { document_id: doc.id, title: doc.name, category: doc.category }); } }}
                        className={`w-full flex items-center gap-3 p-3.5 bg-cream rounded-xl border border-cream-dark hover:border-forest-200 transition-all duration-200 ease-out group text-left${draggingDocId === doc.id ? ' opacity-40 scale-[0.99]' : ''}${animatingOut ? ' opacity-0 -translate-x-3' : ' opacity-100 translate-x-0'}`}>
                        {canReorderLibrary && (
                          <span className="material-symbols-outlined text-[20px] text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing">drag_handle</span>
                        )}
                        <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0"><FileText className="w-4 h-4 text-slate-500" /></div>
                        <div className="flex-1 min-w-0">
                          <div className="text-slate-900 text-sm font-medium flex items-center gap-1.5">
                            <span className="truncate">{doc.name}</span>
                            {showStatusDots && (() => {
                              const status = doc.ai_status || 'pending';
                              const dots = {
                                live: { color: 'bg-forest-500', tooltip: 'AI searchable' },
                                pending: { color: 'bg-amber-400', tooltip: 'Awaiting AI validation' },
                                validating: { color: 'bg-blue-400', tooltip: 'Validating...' },
                                failed: { color: 'bg-red-400', tooltip: 'Validation failed' },
                                superseded: { color: 'bg-slate-400', tooltip: 'Superseded' },
                                excluded: { color: 'bg-slate-300', tooltip: 'Excluded from AI search' },
                              };
                              const dot = dots[status] || dots.pending;
                              return (
                                <span
                                  className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 cursor-help ${dot.color}`}
                                  title={dot.tooltip}
                                />
                              );
                            })()}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-slate-500">{doc.type}</span>
                            <span className="text-slate-300">•</span>
                            <span className="text-xs text-slate-400">{doc.size}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {!readOnly && (
                            <>
                              <button onClick={(e) => { e.stopPropagation(); setEditingDocument({ ...doc, folderKey: getDocumentFolderKey(doc) }); }}
                                className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-all"><Edit3 className="w-4 h-4" /></button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleRevalidate(doc); }}
                                className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-forest-600 transition-all"
                                title="Re-validate for AI search"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="23 4 23 10 17 10"></polyline>
                                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                                </svg>
                              </button>
                            </>
                          )}
                          <button onClick={(e) => {
                            e.stopPropagation();
                            const link = document.createElement('a');
                            link.href = doc.content;
                            link.download = doc.name;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                            className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-forest-100 text-forest-600 transition-all"><Download className="w-4 h-4" /></button>
                        </div>
                      </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12"><Folder className="w-12 h-12 text-slate-200 mx-auto mb-3" /><h3 className="text-slate-700 text-lg font-medium mb-1">No documents yet</h3><p className="text-sm text-slate-400">Use the Upload button above to add documents.</p></div>
              )
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center"><FileText className="w-12 h-12 text-slate-200 mx-auto mb-3" /><h2 className="text-lg font-semibold text-slate-700 mb-1">Select a Folder</h2><p className="text-sm text-slate-400">Choose a folder from the left to view documents.</p></div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Full-Screen Document Preview Modal */}
      <AnimatePresence>
        {selectedDocument && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center" onClick={() => { setSelectedDocument(null); setPdfSearchQuery(''); }}>
            <button onClick={() => { setSelectedDocument(null); setPdfSearchQuery(''); }} className="absolute top-4 right-4 z-10 bg-white rounded-full p-2 shadow-lg text-slate-600 hover:text-slate-900 transition-colors"><X className="w-5 h-5" /></button>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              className="w-full h-full max-w-5xl max-h-[92vh] m-4 bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
              {!selectedDocument.content ? (
                <div className="flex flex-col items-center justify-center h-full text-center p-12">
                  <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-700 mb-2">{selectedDocument.name}</h3>
                  <p className="text-slate-500 mb-2">This document's file is not available in storage.</p>
                  <p className="text-sm text-slate-400">It may have been inserted directly into the database without uploading the actual file. Please delete and re-upload through the Upload button.</p>
                  <button onClick={() => { setSelectedDocument(null); setEditingDocument({ ...selectedDocument, folderKey: getDocumentFolderKey(selectedDocument) }); }} className="mt-6 px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-xl text-sm">Edit this record</button>
                </div>
              ) : selectedDocument.type === 'PDF' ? (
                <PdfViewer
                  document={selectedDocument}
                  onClose={() => { setSelectedDocument(null); setPdfSearchQuery(''); }}
                />
              ) : (
                <>
                  <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-cream flex-shrink-0">
                    <div><h3 className="text-slate-900 font-semibold text-lg">{selectedDocument.name}</h3><p className="text-slate-500 text-sm">{selectedDocument.category} • {selectedDocument.size}</p></div>
                    <div className="flex items-center gap-4">
                      <button onClick={() => {
                        const link = document.createElement('a');
                        link.href = selectedDocument.content;
                        link.download = selectedDocument.name;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center" title="Download"><Download className="w-5 h-5" /></button>
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto">
                    {selectedDocument.type === 'Image' || selectedDocument.type === 'TIFF' ? (
                      <div className="flex items-center justify-center h-full p-8 bg-slate-50"><TiffImage src={selectedDocument.content} alt={selectedDocument.name} className="max-w-full max-h-full object-contain rounded-lg" /></div>
                    ) : isWordDocument(selectedDocument) ? (
                      <OfficeDocumentPreview document={selectedDocument} />
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center"><FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" /><h3 className="text-xl font-semibold text-slate-700 mb-2">Preview Not Available</h3><p className="text-slate-500 mb-4">This file type cannot be previewed.</p>
                          <button onClick={() => window.open(selectedDocument.content, '_blank')} className="flex items-center gap-2 px-4 py-2 bg-forest-700 text-white rounded-xl hover:bg-forest-600 mx-auto"><Download className="w-4 h-4" /> Download</button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── CONFIRM DIALOGS ──────────────────────────────────── */}
      {/* Page-level only: do not move this ConfirmDialog inside another <Modal>; use an inline panel there. */}
      <ConfirmDialog
        isOpen={!!pendingDeleteDocId}
        title="Delete Document"
        message="This will permanently remove the document record and its file from storage. This cannot be undone."
        confirmLabel="Yes, delete"
        cancelLabel="Cancel"
        onConfirm={() => handleDeleteDocument(pendingDeleteDocId)}
        onCancel={() => setPendingDeleteDocId(null)}
      />
      {/* Page-level only: do not move this ConfirmDialog inside another <Modal>; use an inline panel there. */}
      <ConfirmDialog
        isOpen={!!pendingDeleteFolderId}
        title="Delete Folder"
        message="Only empty folders can be deleted. If this folder contains documents, move them to another folder first."
        confirmLabel="Delete empty folder"
        cancelLabel="Cancel"
        onConfirm={() => { handleDeleteFolder(pendingDeleteFolderId); setPendingDeleteFolderId(null); }}
        onCancel={() => setPendingDeleteFolderId(null)}
      />
      {/* Delete error banner */}
      <AnimatePresence>
        {deleteError && (
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] bg-red-600 text-white px-5 py-3 rounded-xl shadow-2xl flex items-center gap-3 max-w-sm">
            <AlertCircle className="w-5 h-5 shrink-0" />
            <p className="text-sm flex-1">{deleteError}</p>
            <button onClick={() => setDeleteError(null)} className="text-white/70 hover:text-white"><X className="w-4 h-4" /></button>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Revalidate info banner */}
      <AnimatePresence>
        {revalidateMessage && (
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] bg-amber-600 text-white px-5 py-3 rounded-xl shadow-2xl flex items-center gap-3 max-w-sm">
            <AlertCircle className="w-5 h-5 shrink-0" />
            <p className="text-sm flex-1">{revalidateMessage}</p>
            <button onClick={() => setRevalidateMessage(null)} className="text-white/70 hover:text-white"><X className="w-4 h-4" /></button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Upload Modal */}
      <AnimatePresence>
        {showUpload && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => !isValidating && handleSkipLevel4()}>
            {/* Custom upload overlay, not a nested <Modal>; use an inline panel if this ever moves into Modal. */}
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              className="bg-white rounded-[2rem] md:rounded-2xl warm-tactile-shadow w-full md:max-w-2xl h-[calc(100dvh-2rem)] md:h-auto md:max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center px-6 pt-6 pb-4 border-b border-slate-100 shrink-0">
                <h3 className="text-xl font-bold text-slate-800">Upload Documents</h3>
                <button onClick={() => !isValidating && handleSkipLevel4()} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-6 overflow-y-auto flex-1">
                <div {...getRootProps()} className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${isDragActive ? 'border-forest-500 bg-forest-50' : 'border-slate-300 hover:border-slate-400 hover:bg-cream'}`}>
                  <input {...getInputProps()} />
                  <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">{isDragActive ? 'Drop files here' : 'Upload Documents'}</h3>
                  <p className="text-slate-500">Drag and drop files, or click to browse</p>
                </div>
                <div className="mt-6">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">Choose Destination Folder</h3>
                  <div className="flex gap-3">
                    <select value={uploadModalFolder?.id || ''} onChange={(e) => { const folderId = e.target.value; const folder = Object.entries(folderStructure).find(([key]) => key === folderId); setUploadModalFolder(folder ? { id: folderId, ...folder[1] } : null); }}
                      required className="flex-1 px-3.5 py-2.5 bg-cream border border-cream-dark rounded-xl text-slate-700 text-base md:text-sm outline-none focus:border-forest-500 focus:ring-2 focus:ring-forest-100">
                      <option value="" disabled>Select a folder…</option>
                      {Object.entries(folderStructure).filter(([key]) => key !== 'All').map(([key, folder]) => (<option key={key} value={key}>{folder.name}</option>))}
                    </select>
                    <button onClick={() => setShowCreateFolder(true)} className="px-4 py-2 bg-forest-700 hover:bg-forest-600 text-white rounded-xl text-sm flex items-center gap-2"><Plus className="w-4 h-4" /> New Folder</button>
                  </div>
                </div>
                {uploadError && (
                  <div className="mt-4 bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-red-700">Upload failed</p>
                      <p className="text-xs text-red-600 mt-0.5 break-words">{uploadError}</p>
                    </div>
                    <button onClick={() => setUploadError(null)} className="text-red-400 hover:text-red-600 shrink-0"><X className="w-4 h-4" /></button>
                  </div>
                )}
                {uploadFiles.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">Files to Upload</h3>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {uploadFiles.map((file, i) => (
                        <div key={i} className="p-3 bg-cream rounded-xl border border-cream-dark">
                          <div className="flex items-center gap-3">
                          <FileText className="w-6 h-6 text-forest-500 flex-shrink-0" />
                          <div className="flex-1 min-w-0"><p className="text-sm font-medium text-slate-800 truncate">{file.name}</p><p className="text-xs text-slate-500">{file.size} • {file.type}</p></div>
                          <button onClick={() => setUploadFiles(prev => prev.filter((_, idx) => idx !== i))} className="p-1 text-slate-400 hover:text-red-500 min-h-[44px] min-w-[44px] flex items-center justify-center" disabled={isUploading}><X className="w-4 h-4" /></button>
                          </div>
                          <label className="mt-3 flex items-center justify-between gap-3 rounded-xl bg-white/70 border border-white px-3 py-2 cursor-pointer">
                            <span>
                              <span className="block text-xs font-bold text-primary-dark">Visible to homeowners</span>
                              <span className="block text-[11px] font-medium text-slate-500">Off by default. Enable only after homeowner review.</span>
                            </span>
                            <span className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${file.visibleToHomeowners ? 'bg-secondary' : 'bg-slate-300'}`}>
                              <input
                                type="checkbox"
                                checked={file.visibleToHomeowners}
                                onChange={(event) => setUploadFiles(prev => prev.map((item, idx) => idx === i ? { ...item, visibleToHomeowners: event.target.checked } : item))}
                                className="sr-only"
                                disabled={isUploading}
                              />
                              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${file.visibleToHomeowners ? 'translate-x-6' : 'translate-x-1'}`} />
                            </span>
                          </label>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-3 mt-4">
                      <button
                        onClick={handleFileUpload}
                        disabled={isUploading || !uploadModalFolder || uploadModalFolder.id === 'All' || uploadFiles.length === 0}
                        className="flex-1 px-4 py-2.5 bg-forest-700 hover:bg-forest-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
                      >
                        {isUploading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            {uploadStatusText || 'Uploading…'}
                          </>
                        ) : (
                          <>Upload {uploadFiles.length} File{uploadFiles.length !== 1 ? 's' : ''}</>
                        )}
                      </button>
                      <button onClick={() => { setUploadFiles([]); queuedFileNames.current.clear(); }} disabled={isUploading} className="px-4 py-2.5 bg-red-50 text-red-600 border border-red-200 rounded-xl text-sm disabled:opacity-50">Clear</button>
                    </div>
                  </div>
                )}

                <ValidationPanel />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create Folder Modal */}
      <AnimatePresence>
        {showCreateFolder && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => { setShowCreateFolder(false); setNewFolderName(''); }}>
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Create New Folder</h3>
              <input type="text" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} placeholder="Enter folder name..."
                className="w-full px-3.5 py-2.5 bg-cream border border-cream-dark rounded-xl text-base md:text-sm outline-none focus:border-forest-500 focus:ring-2 focus:ring-forest-100"
                onKeyPress={(e) => e.key === 'Enter' && handleCreateFolder()} autoFocus />
              <div className="flex gap-3 pt-4 mt-4 border-t border-slate-100">
                <button onClick={() => { setShowCreateFolder(false); setNewFolderName(''); }} className="px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl text-sm">Cancel</button>
                <button onClick={handleCreateFolder} disabled={!newFolderName.trim()} className="px-4 py-2.5 bg-forest-700 hover:bg-forest-600 disabled:bg-slate-300 text-white rounded-xl text-sm font-semibold">Create</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Document Modal */}
      <AnimatePresence>
        {editingDocument && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setEditingDocument(null)}>
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Edit Document</h3>
              <div className="space-y-4">
                <div><label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">Name</label>
                  <input type="text" value={editingDocument.name} onChange={(e) => setEditingDocument({ ...editingDocument, name: e.target.value })}
                    className="w-full px-3.5 py-2.5 bg-cream border border-cream-dark rounded-xl text-base md:text-sm outline-none focus:border-forest-500 focus:ring-2 focus:ring-forest-100" /></div>
                <div><label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">Folder</label>
                  <select value={editingDocument.folderKey || getDocumentFolderKey(editingDocument)} onChange={(e) => setEditingDocument({ ...editingDocument, folderKey: e.target.value })}
                    className="w-full px-3.5 py-2.5 bg-cream border border-cream-dark rounded-xl text-base md:text-sm outline-none focus:border-forest-500 focus:ring-2 focus:ring-forest-100">
                    {Object.entries(folderStructure).filter(([key]) => key !== 'All').map(([key, folder]) => (<option key={key} value={key}>{folder.name}</option>))}
                  </select></div>
                <label className="flex items-center justify-between gap-4 rounded-2xl bg-section-bg border border-amber-100/30 px-4 py-3 cursor-pointer">
                  <span>
                    <span className="block text-sm font-bold text-primary-dark">Visible to homeowners</span>
                    <span className="block text-xs font-medium text-slate-500 mt-0.5">Controls whether member accounts can see this document.</span>
                  </span>
                  <span className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${editingDocument.visible_to_homeowners ? 'bg-secondary' : 'bg-slate-300'}`}>
                    <input
                      type="checkbox"
                      checked={!!editingDocument.visible_to_homeowners}
                      onChange={(e) => setEditingDocument({ ...editingDocument, visible_to_homeowners: e.target.checked })}
                      className="sr-only"
                    />
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${editingDocument.visible_to_homeowners ? 'translate-x-6' : 'translate-x-1'}`} />
                  </span>
                </label>
              </div>

              <div className="pt-4 border-t border-slate-100 flex gap-3" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
                <button onClick={() => handleUpdateDocument(editingDocument.id, { title: editingDocument.name, ...buildDocumentFolderUpdates(editingDocument.folderKey || getDocumentFolderKey(editingDocument), editingDocument), visible_to_homeowners: !!editingDocument.visible_to_homeowners })}
                  className="flex-1 px-4 py-2.5 bg-forest-700 hover:bg-forest-600 text-white rounded-xl text-sm font-semibold">Save</button>
                <button onClick={() => { setPendingDeleteDocId(editingDocument.id); }}
                  className="px-4 py-2.5 bg-red-50 text-red-600 border border-red-200 rounded-xl text-sm">Delete</button>
                <button onClick={() => setEditingDocument(null)} className="px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl text-sm">Cancel</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Folder Modal */}
      <AnimatePresence>
        {editingFolder && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setEditingFolder(null)}>
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Edit Folder</h3>
              <input type="text" value={editingFolder.name} onChange={(e) => setEditingFolder({ ...editingFolder, name: e.target.value })}
                className="w-full px-3.5 py-2.5 bg-cream border border-cream-dark rounded-xl text-base md:text-sm outline-none focus:border-forest-500 focus:ring-2 focus:ring-forest-100"
                onKeyPress={(e) => { if (e.key === 'Enter') handleUpdateFolder(editingFolder.id, editingFolder.name); }} />
              <div className="flex gap-3 mt-6 pt-4 border-t border-slate-100">
                <button onClick={() => handleUpdateFolder(editingFolder.id, editingFolder.name)} className="flex-1 px-4 py-2.5 bg-forest-700 hover:bg-forest-600 text-white rounded-xl text-sm font-semibold">Save</button>
                <button onClick={() => { setPendingDeleteFolderId(editingFolder.id); }}
                  className="px-4 py-2.5 bg-red-50 text-red-600 border border-red-200 rounded-xl text-sm">Delete</button>
                <button onClick={() => setEditingFolder(null)} className="px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl text-sm">Cancel</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DocumentManagement;
